import type {
  AppointmentItem,
  CallLogItem,
  ChatLogItem,
  InteractionHistory,
  NoteItem,
  PipelineStage,
  TaskItem,
} from 'types/entity'
import { calendarDaysBetween, formatWhen, hasClockTime, parseWhen, relativeDay } from './parseWhen'

/**
 * The brief: what a person needs to know before they speak to this customer, worked out from
 * what the record already holds.
 *
 * The screen has always had every fact below - spread over eleven tabs, each fetched on click,
 * each in its own table. Reading them all and holding the story in your head was the agent's
 * job. This does that reading. It adds no data and asks nobody to type anything new.
 *
 * Three rules it never breaks:
 *   1. Only what the record says. Every line traces to an entry the user can open in the
 *      timeline below it. Nothing is inferred about what the customer "probably" wants.
 *   2. Unknown stays unknown. A channel that failed to load is named as unavailable - it is
 *      never read as "no calls". A date that cannot be read takes no part in any conclusion.
 *   3. Pure function. Same inputs, same brief; `now` is passed in. That is what lets the rules
 *      below be tested line by line (buildBrief.test.ts).
 */

export type Channel = 'followup' | 'call' | 'whatsapp' | 'chat' | 'note' | 'activity'

export const CHANNEL_LABEL: Record<Channel, string> = {
  followup: 'Follow-up',
  call: 'Call',
  whatsapp: 'WhatsApp',
  chat: 'Chat',
  note: 'Note',
  activity: 'Activity',
}

export interface TimelineEntry {
  id: string
  channel: Channel
  /** null when the source's date could not be read - listed last, never used in a conclusion. */
  when: Date | null
  /** The date exactly as the source gave it; shown when `when` is null. */
  whenText: string
  hasTime: boolean
  title: string
  body: string
  who: string
  /** Set only where the source states it (WhatsApp, chat). */
  direction?: 'in' | 'out'
  /** A call nobody picked up: an attempt, not a conversation. */
  unanswered?: boolean
}

export type NextStepKind = 'followup' | 'task' | 'appointment'

export interface NextStep {
  id: string
  kind: NextStepKind
  label: string
  when: Date
  hasTime: boolean
  who: string
  /** Whole days past due; 0 when due today or later. */
  overdueDays: number
  dueToday: boolean
}

export interface Person {
  name: string
  touches: number
  last: Date | null
  isYou: boolean
}

export type Tone = 'danger' | 'warn' | 'good' | 'neutral'

/** A deal that has sat in one stage for too long. Derived from the record's own deal stages. */
export interface StuckDeal {
  pipeline: string
  stage: string
  /** When the deal reached this stage. */
  since: Date
  days: number
}

export interface Brief {
  headline: { tone: Tone; text: string }
  /** Latest exchange with the customer - calls, messages, follow-ups. Notes are internal and excluded. */
  lastContact: TimelineEntry | null
  /** The most recent follow-up that has a remark: the last thing anyone wrote down about this customer. */
  lastWords: TimelineEntry | null
  latestNote: TimelineEntry | null
  /** The customer's latest WhatsApp message, when nothing went back to them after it. */
  waitingOnUs: TimelineEntry | null
  nextSteps: NextStep[]
  /** Open deals whose current stage has not moved for STUCK_DEAL_DAYS or more, longest first. */
  stuckDeals: StuckDeal[]
  people: Person[]
  /** True when somebody has worked this record and none of the entries is under the viewer's name. */
  newToYou: boolean
  timeline: TimelineEntry[]
  /** Channels that failed to load. Their absence from the brief means "not known", not "none". */
  unavailable: string[]
}

export interface BriefSources {
  /**
   * Names as the record itself spells them - owner, created-by. Optional. They are read only to
   * learn that "Mukesh Kumar (kmukesh343)" makes "Mukesh Kumar" and "kmukesh343" one person.
   */
  nameHints?: string[]
  /** Each is null when that channel could not be loaded, [] when it loaded and is empty. */
  interactions: InteractionHistory[] | null
  calls: CallLogItem[] | null
  whatsapp: ChatLogItem[] | null
  chats: ChatLogItem[] | null
  notes: NoteItem[] | null
  tasks: TaskItem[] | null
  appointments: AppointmentItem[] | null
  /** The record's deal stages (lead only); [] when it has no deal, null when they could not be read. */
  deals?: PipelineStage[] | null
}

export interface Viewer {
  loginName: string
  displayName: string
}

/**
 * A follow-up status that ends the chase. For these the API's "next status date" is the
 * conversion / closure date, not a promise to call back, so it must not be reported as due.
 * The status master is per tenant and its flags are not on this response, so this reads the
 * wording. Widening it only ever hides a reminder; it cannot invent one.
 */
const TERMINAL_STATUS_RE = /\b(convert\w*|closed?|won|lost|dead|junk|invalid|not\s+interested|dnd|duplicate)\b/i

/** Call outcomes where the two sides never spoke. A blank or unknown status is not assumed to be one. */
const NOT_CONNECTED_RE = /\b(no\s*answer|not\s*answered|unanswered|busy|failed?|missed|cancel\w*|congestion|rejected|unreachable)\b/i

/** A deal that sits in one stage this long is no longer moving. */
export const STUCK_DEAL_DAYS = 14
/** Past this it is a problem, not a note. */
const STUCK_DEAL_DANGER_DAYS = 30

const FINISHED_RE = /\b(complet\w*|closed?|cancel\w*|done|resolved)\b/i

const clean = (value: unknown): string => String(value ?? '').trim()

function entry(
  channel: Channel, id: string, whenText: string,
  title: string, body: string, who: string, direction?: 'in' | 'out',
): TimelineEntry {
  return {
    id: `${channel}:${id}`,
    channel,
    when: parseWhen(whenText),
    whenText: clean(whenText),
    hasTime: hasClockTime(whenText),
    title: clean(title),
    body: clean(body),
    who: clean(who),
    direction,
  }
}

/**
 * The conversation feed stamps its times "...Z" but they are the account's wall-clock time, not
 * UTC: a lead created at 16:35:13 has its chat "started" at 16:35:15Z. Read as UTC they land
 * 5h30 late in India and put the whole timeline out of order, so the false zone is dropped.
 */
function wallClock(text: string): string {
  return clean(text).replace(/Z$/i, '')
}

function newestFirst(a: TimelineEntry, b: TimelineEntry): number {
  if (a.when && b.when) return b.when.getTime() - a.when.getTime()
  if (a.when) return -1
  if (b.when) return 1
  return 0
}

function toTimeline(src: BriefSources): TimelineEntry[] {
  const out: TimelineEntry[] = []

  ;(src.interactions ?? []).forEach((row, i) =>
    out.push(entry('followup', String(i), row.CreatedOn, row.FollowupStatus, row.Remarks, row.CreatedBy)),
  )
  ;(src.calls ?? []).forEach((call) => {
    const unanswered = NOT_CONNECTED_RE.test(clean(call.status))
    const outcome = unanswered ? clean(call.status).toLowerCase() : call.duration
    const item = entry('call', call.id, call.callTime, [call.callType, outcome].filter(Boolean).join(' · '), '', call.agent)
    out.push(unanswered ? { ...item, unanswered } : item)
  })
  ;(src.whatsapp ?? []).forEach((msg) => {
    const at = wallClock(msg.sentOn)
    if (msg.system) {
      // The feed's own log line. Not a message: no direction, and its "AgentName" is not an author.
      out.push(entry('activity', `wa-${msg.id}`, at, '', msg.message, ''))
      return
    }
    const kind = clean(msg.messageType).toLowerCase()
    const body = clean(msg.message) || (kind && kind !== 'text' ? `(${kind})` : '')
    // The feed labels every outbound message's agent "You" - bot replies included - so it names nobody.
    const who = /^you$/i.test(clean(msg.sender)) ? '' : msg.sender
    out.push(entry('whatsapp', msg.id, at, msg.direction === 'in' ? 'Customer wrote' : 'We wrote', body, who, msg.direction))
  })
  ;(src.chats ?? []).forEach((msg) =>
    out.push(entry('chat', msg.id, msg.sentOn, 'Chat', msg.message, msg.sender, msg.direction)),
  )
  ;(src.notes ?? []).forEach((note) => out.push(entry('note', note.id, note.createdOn, 'Note', note.note, note.createdBy)))

  return out.sort(newestFirst)
}

function toNextSteps(src: BriefSources, now: Date): NextStep[] {
  const steps: NextStep[] = []
  const push = (id: string, kind: NextStepKind, label: string, whenText: string, who: string) => {
    const when = parseWhen(whenText)
    if (!when) return
    const timed = hasClockTime(whenText)
    const days = calendarDaysBetween(when, now)
    // With a clock time, "overdue" starts the minute it passes; without one, only the next day.
    const late = timed ? when.getTime() < now.getTime() : days > 0
    steps.push({
      id: `${kind}:${id}`, kind, label: clean(label), when, hasTime: timed, who: clean(who),
      overdueDays: late ? Math.max(days, 0) : 0,
      dueToday: days === 0,
    })
  }

  // Only the newest follow-up speaks for the record: each new one replaces the last one's date.
  const newest = (src.interactions ?? [])
    .map((row) => ({ row, at: parseWhen(row.CreatedOn) }))
    .filter((x): x is { row: InteractionHistory; at: Date } => x.at !== null)
    .sort((a, b) => b.at.getTime() - a.at.getTime())[0]
  if (newest && !TERMINAL_STATUS_RE.test(clean(newest.row.FollowupStatus))) {
    push('current', 'followup', clean(newest.row.FollowupStatus) || 'Follow-up', newest.row.NextStatusDate, newest.row.AssignedTo)
  }

  ;(src.tasks ?? [])
    .filter((task) => !FINISHED_RE.test(clean(task.status)))
    .forEach((task) => push(task.id, 'task', task.subject || 'Task', task.dueDate, task.assignedTo))

  ;(src.appointments ?? [])
    .filter((ap) => !FINISHED_RE.test(clean(ap.status)))
    .forEach((ap) => {
      const start = parseWhen(ap.startDate)
      // A past appointment that was never closed is history, not a plan.
      if (start && start.getTime() >= now.getTime()) push(ap.id, 'appointment', ap.subject || 'Appointment', ap.startDate, ap.assignedTo)
    })

  // Most overdue first, then soonest.
  return steps.sort((a, b) => b.overdueDays - a.overdueDays || a.when.getTime() - b.when.getTime())
}

/**
 * A deal's current stage is its last COMPLETED one (PipeLineStage non-empty - the same rule the
 * pipeline strip uses), per pipeline. It is stuck when that stage was reached STUCK_DEAL_DAYS ago
 * or more and does not end the deal (won / lost / closed, read from the name - the stage master
 * is per tenant and carries no flag here). A pipeline with no completed stage is a deal that was
 * never started, not a stuck one.
 */
function toStuckDeals(stages: PipelineStage[] | null | undefined, now: Date): StuckDeal[] {
  const byPipeline = new Map<string, { stage: string; at: Date }>()
  for (const s of stages ?? []) {
    if (!s.PipeLineStage) continue
    const at = parseWhen(s.CreatedDate)
    if (!at) continue
    const key = clean(s.PinelineName) || '(pipeline)'
    const seen = byPipeline.get(key)
    if (!seen || at > seen.at) byPipeline.set(key, { stage: clean(s.StageName), at })
  }
  const out: StuckDeal[] = []
  for (const [pipeline, cur] of byPipeline) {
    if (TERMINAL_STATUS_RE.test(cur.stage)) continue
    const days = calendarDaysBetween(cur.at, now)
    if (days >= STUCK_DEAL_DAYS) out.push({ pipeline, stage: cur.stage, since: cur.at, days })
  }
  return out.sort((a, b) => b.days - a.days)
}

/** "Mukesh Kumar (kmukesh343)" -> ["Mukesh Kumar", "kmukesh343"]; "anil" -> ["anil"]. */
function nameParts(name: string): string[] {
  const paired = /^(.*?)\s*\((.*?)\)\s*$/.exec(name.trim())
  const parts = paired ? [paired[1], paired[2]] : [name]
  return parts.map((part) => part.trim()).filter(Boolean)
}

/**
 * One person, however the API spells them. The same colleague arrives as "kmukesh343" on a call,
 * "Mukesh Kumar" on a follow-up and "Mukesh Kumar (kmukesh343)" on the record. Any string that
 * pairs two spellings - and the signed-in user's own login + display name - ties them together.
 * Two spellings nothing ever pairs stay two people: merging on a guess would credit one person
 * with another's work.
 */
function buildAliases(pairs: string[][]): (name: string) => string {
  const group = new Map<string, string>()
  const find = (key: string): string => {
    let root = key
    while (group.get(root) !== root) root = group.get(root) as string
    return root
  }
  for (const parts of pairs) {
    const keys = parts.map((part) => part.toLowerCase())
    keys.forEach((key) => { if (!group.has(key)) group.set(key, key) })
    for (let i = 1; i < keys.length; i += 1) group.set(find(keys[i]), find(keys[0]))
  }
  return (name) => {
    const keys = nameParts(name).map((part) => part.toLowerCase())
    const known = keys.find((key) => group.has(key))
    return known ? find(known) : keys[0] ?? ''
  }
}

function toPeople(timeline: TimelineEntry[], viewer: Viewer, nameHints: string[]): Person[] {
  const viewerNames = [viewer.loginName, viewer.displayName].map((n) => n.trim()).filter(Boolean)
  const canonical = buildAliases([
    viewerNames,
    ...nameHints.map(nameParts),
    ...timeline.map((item) => nameParts(item.who)),
  ])
  const viewerKey = viewerNames.length > 0 ? canonical(viewerNames[0]) : null
  const logins = new Set(viewer.loginName.trim() ? [viewer.loginName.trim().toLowerCase()] : [])

  const byKey = new Map<string, Person>()
  for (const item of timeline) {
    // An inbound message is "from" the customer; they are not one of our people.
    if (item.direction === 'in' || item.who === '' || /^(system|bot)$/i.test(item.who)) continue
    const key = canonical(item.who)
    const person = byKey.get(key) ?? { name: '', touches: 0, last: null, isYou: key === viewerKey }
    // Show the fullest human name seen: prefer a spelling that is not the bare login.
    const best = nameParts(item.who).find((part) => !logins.has(part.toLowerCase()) && /\s/.test(part)) ?? nameParts(item.who)[0]
    if (person.name === '' || (!/\s/.test(person.name) && /\s/.test(best))) person.name = best
    person.touches += 1
    if (item.when && (!person.last || item.when > person.last)) person.last = item.when
    byKey.set(key, person)
  }
  return [...byKey.values()].sort((a, b) => (b.last?.getTime() ?? 0) - (a.last?.getTime() ?? 0))
}

function findWaitingOnUs(timeline: TimelineEntry[]): TimelineEntry | null {
  const lastInbound = timeline.find((t) => t.channel === 'whatsapp' && t.direction === 'in' && t.when)
  if (!lastInbound?.when) return null
  const at = lastInbound.when.getTime()
  // Anything we did towards the customer afterwards counts as an answer - a reply, a call, a follow-up.
  const answered = timeline.some(
    (t) => t.when && t.when.getTime() > at && t.channel !== 'note' && t.channel !== 'activity' && !t.unanswered && t.direction !== 'in',
  )
  return answered ? null : lastInbound
}

function headline(brief: Omit<Brief, 'headline'>, now: Date): Brief['headline'] {
  const { waitingOnUs, nextSteps, lastContact, timeline } = brief

  if (waitingOnUs?.when) {
    return { tone: 'danger', text: `Waiting on us — the customer wrote on WhatsApp ${relativeDay(waitingOnUs.when, now)} and nothing has gone back since.` }
  }

  const overdue = nextSteps.filter((s) => s.overdueDays > 0 || (s.hasTime && s.when < now && s.dueToday))
  if (overdue.length > 0) {
    const worst = overdue[0]
    const what = worst.kind === 'followup' ? 'Follow-up' : worst.kind === 'task' ? `Task "${worst.label}"` : worst.label
    const howLate = worst.overdueDays > 0 ? `overdue by ${worst.overdueDays} day${worst.overdueDays === 1 ? '' : 's'}` : 'was due earlier today'
    const more = overdue.length > 1 ? ` (+${overdue.length - 1} more overdue)` : ''
    return { tone: 'danger', text: `${what} ${howLate} — due ${formatWhen(worst.when, worst.hasTime)}${more}.` }
  }

  const stuck = brief.stuckDeals[0]
  if (stuck && stuck.days >= STUCK_DEAL_DANGER_DAYS) {
    return { tone: 'danger', text: `Deal stuck: "${stuck.stage}" in ${stuck.pipeline} for ${stuck.days} days — it has not moved since ${formatWhen(stuck.since, false)}.` }
  }

  const today = nextSteps.find((s) => s.dueToday)
  if (today) {
    const at = today.hasTime ? ` at ${formatWhen(today.when, true).split(', ')[1]}` : ''
    return { tone: 'warn', text: `Due today${at}: ${today.kind === 'followup' ? 'follow-up' : today.label}.` }
  }

  if (stuck) {
    return { tone: 'warn', text: `Deal not moving: "${stuck.stage}" in ${stuck.pipeline} for ${stuck.days} days.` }
  }

  if (nextSteps.length === 0 && timeline.length > 0) {
    const since = lastContact?.when ? ` Last contact ${relativeDay(lastContact.when, now)}.` : ''
    return { tone: 'warn', text: `No next step is set on this record.${since}` }
  }

  if (nextSteps.length > 0) {
    const next = nextSteps[0]
    return { tone: 'good', text: `Next: ${next.kind === 'followup' ? 'follow-up' : next.label} ${relativeDay(next.when, now)} (${formatWhen(next.when, next.hasTime)}).` }
  }

  return { tone: 'neutral', text: 'Nothing is recorded yet — this will be the first conversation.' }
}

const CHANNEL_NAMES: Array<[Exclude<keyof BriefSources, 'nameHints'>, string]> = [
  ['interactions', 'follow-ups'],
  ['calls', 'calls'],
  ['whatsapp', 'WhatsApp'],
  ['chats', 'chat'],
  ['notes', 'notes'],
  ['tasks', 'tasks'],
  ['appointments', 'appointments'],
  ['deals', 'deals'],
]


export function buildBrief(src: BriefSources, viewer: Viewer, now: Date): Brief {
  const timeline = toTimeline(src)
  const people = toPeople(timeline, viewer, src.nameHints ?? [])
  const viewerKnown = viewer.loginName.trim() !== '' || viewer.displayName.trim() !== ''

  const partial: Omit<Brief, 'headline'> = {
    lastContact: timeline.find((t) => t.channel !== 'note' && t.channel !== 'activity' && !t.unanswered && t.when) ?? null,
    lastWords: timeline.find((t) => t.channel === 'followup' && t.body !== '' && t.when) ?? null,
    latestNote: timeline.find((t) => t.channel === 'note' && t.when) ?? null,
    waitingOnUs: findWaitingOnUs(timeline),
    nextSteps: toNextSteps(src, now),
    stuckDeals: toStuckDeals(src.deals, now),
    people,
    newToYou: viewerKnown && people.length > 0 && !people.some((p) => p.isYou),
    timeline,
    unavailable: CHANNEL_NAMES.filter(([key]) => src[key] === null).map(([, label]) => label),
  }

  return { ...partial, headline: headline(partial, now) }
}
